import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import { useFetchHistorialCambio } from "@/shared/actions/controlcambios/cambio-service.actions";

type Props = {
  open: boolean;
  onClose: () => void;
  cambioId: string;
  codigoCambio?: string;
};

const ACCION_COLOR: Record<string, "default" | "info" | "success" | "warning" | "error"> = {
  CREACION: "info",
  CREADO: "info",
  ACTUALIZACIÓN: "warning",
  ACTUALIZACION: "warning",
  ACTUALIZADO: "warning",
  APROBADO: "success",
  APROBACION: "success",
  RECHAZADO: "error",
  RECHAZO: "error",
  EN_EJECUCION: "warning",
  IMPLEMENTADO: "success",
  CANCELADO: "error",
};

function getChipColor(accion: string) {
  const key = accion?.toUpperCase().replace(/\s+/g, "_");
  return ACCION_COLOR[key] ?? "default";
}

function formatFecha(fecha: string) {
  if (!fecha) return "—";
  const d = new Date(fecha);
  return d.toLocaleString("es-EC", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function HistorialCambioModal({ open, onClose, cambioId, codigoCambio }: Props) {
  const { data: historial, isLoading } = useFetchHistorialCambio(cambioId, open);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <HistoryIcon color="primary" />
        Historial del cambio{codigoCambio ? ` — ${codigoCambio}` : ""}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !historial || historial.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography color="text.secondary">Sin registros de historial</Typography>
          </Box>
        ) : (
          <Box>
            {historial.map((item, index) => (
              <Box key={item.historialCambioId}>
                <Box sx={{ px: 3, py: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
                  {/* línea de tiempo */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 24 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        mt: "4px",
                        flexShrink: 0,
                      }}
                    />
                    {index < historial.length - 1 && (
                      <Box sx={{ width: 2, flex: 1, bgcolor: "divider", minHeight: 24, mt: "4px" }} />
                    )}
                  </Box>

                  {/* contenido */}
                  <Box sx={{ flex: 1, pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                      <Chip
                        label={item.accion}
                        size="small"
                        color={getChipColor(item.accion)}
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatFecha(item.fecha)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                      <strong>Usuario:</strong> {item.usuario}
                    </Typography>
                    {item.detalle && (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {item.detalle}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {index < historial.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
